import { useState } from 'react';
import Button from '../../components/form-control/Button';
import Input from '../../components/form-control/Input';
import { Formik } from 'formik';
import { createTeam } from '../../api/requests';
import { COUNTRIES, COMPANY_TYPES } from '../../resources/enums';
import { ICreateTeamDTO } from '../../resources/interfaces';
import SelectDropdown from '../../components/form-control/SelectDropdown';
import { getCountryIsoFromName, getFormattedTeamTypeFromKey, showToast } from '../../utils/miscFunctions';
import { REGIONS_BY_COUNTRY } from '../../resources/constants';
import usePartner from '../../hooks/partnerHook';
import { useMutation, useQuery } from '@tanstack/react-query';
import useUserAccount from '../../hooks/userAccountHook';

interface CreateTeamProps {
  onProceed: (data: any) => void;
  onClose: () => void;
}
const AddTeam = ({ onProceed, onClose }: CreateTeamProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { getAllSlackWorkspaces } = usePartner();
  const { user } = useUserAccount()
  const { data: workspaces } = useQuery({ queryKey: ['workspaces'], queryFn: getAllSlackWorkspaces });


  const createTeamMutation = useMutation({
    mutationFn: (payload: ICreateTeamDTO) => createTeam(payload),
    onSuccess: (response: any) => {
      showToast.success(response?.message ?? 'Company created successfully');
      onProceed(response);
    },
    onError: (error: any) => {
      showToast.error(error?.description);
    },
    onSettled: () => {
      setSubmitting(false);
    }
  });

  const submitHandler = (values: any) => {
    setSubmitting(true);
    const payload: ICreateTeamDTO = {
      ...values,
      name: values.name,
      city: values.city,
      region: values.region,
      type: values.type,
      countryName: values.countryName,
    };
    createTeamMutation.mutate(payload);
  };


  return (
    <Formik
      initialValues={{
        name: '',
        type: '',
        region: '',
        city: '',
        slackWorkspaceId: '',
        countryName: ''
      }}
      validateOnChange={false}
      validateOnBlur
      onSubmit={(values) => {
        submitHandler(values);
      }}>
      {({
        values,
        errors,
        isSubmitting,
        handleSubmit,
        handleChange,
        setFieldValue,
        handleBlur,
        touched
      }) => (
        <form className="rounded-lg p-4 bg-white dark:bg-darkBg " onSubmit={handleSubmit}>

          <div className="space-y-5 p-5">
            <Input
              label="Team Name"
              placeholder="Enter team name"
              name="name"
              value={values.name}
              type="text"
              wrapperClasses=""
              required
              error={errors.name}
              onChange={handleChange}
              onBlur={handleChange}
              helperText={errors.name}
            />

            <SelectDropdown
              label="Type"
              name="type"
              listSelectedValue={values.type}
              list={Object.values(COMPANY_TYPES)?.map((type: COMPANY_TYPES, id: number) => ({
                name: getFormattedTeamTypeFromKey(type),
                value: type
              }))}
              onChange={(val: string | null) => setFieldValue('type', val)}
              onBlur={handleBlur}
              placeholder="Select team type"
              required
            />

            <SelectDropdown
              label="Country"
              name="countryName"
              listSelectedValue={values.countryName}
              list={Object.values(COUNTRIES)?.map((countryName: COUNTRIES, id: number) => ({
                name: countryName,
                value: countryName
              }))}
              onChange={(val: string | null) => setFieldValue('countryName', val)}
              onBlur={handleBlur}
              placeholder="Select country"
              required
            />

            {values.countryName && <SelectDropdown
              label="Region"
              name="region"
              listSelectedValue={values.region}
              list={REGIONS_BY_COUNTRY[getCountryIsoFromName(values.countryName)]?.map((region: string, id: number) => ({
                name: (region),
                value: region
              }))}
              onChange={(val: string | null) => setFieldValue('region', val)}
              onBlur={handleBlur}
              placeholder="Select team region"
              required
            />}

            {
              values.region &&
              <Input
                label="City"
                placeholder="Enter city"
                name="city"
                value={values.city}
                type="text"
                wrapperClasses=""
                error={errors.city}
                onChange={handleChange}
                onBlur={handleChange}
                helperText={errors.city}
              />
            }

            {user?.isInternalOwner() && (
              <SelectDropdown
                name="slackWorkspaceId"
                label="Slack Workspace"
                placeholder="Select workspace "
                listSelectedValue={values.slackWorkspaceId}
                wrapperClasses=""
                onChange={(value: any) => {
                  setFieldValue('slackWorkspaceId', value);
                }}
                list={workspaces?.map((workspace: any) => ({
                  name: workspace?.name,
                  value: workspace?.id
                }))}
              />
            )}
          </div>

          <div className="w-full flex justify-between gap-4 mt-6 !mb-4">
            <Button
              type="button"
              label="Cancel"
              onClick={onClose}
              bgColor='secondary'
              disabled={submitting}
              fullWidth
            />
            <Button
              type="submit"
              label="Save Company"
              bgColor='primary'
              fullWidth
              loading={submitting}
              disabled={submitting}
            />
          </div>
        </form>
      )}
    </Formik>
  );
};

export default AddTeam;
